import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  markNotificationsSeen,
  type NotificationFeed,
} from '../api/notificationsApi';

const KEY = 'notifications';

/**
 * The feed, for whichever side is asking. `refetchOnMount` because the count is the point: a badge
 * served from a five-minute-old cache would say « 0 » while three refusals are waiting.
 */
export const useNotifications = (enabled = true) =>
  useQuery<NotificationFeed>({
    queryKey: [KEY],
    queryFn: fetchNotifications,
    refetchOnMount: true,
    staleTime: 30_000,
    /**
     * ⚠️ `staleTime` marks data stale; it does not go and fetch it. The topbar never unmounts, so
     * without a poll the count sits at whatever it was when the shell loaded — an operator could
     * work all morning under a bell that never moves.
     *
     * A minute, and only while the tab is in front: the feed is derived, so a poll costs one small
     * read and nothing can desynchronise. `refetchIntervalInBackground` stays off — a background
     * tab has nobody to tell.
     */
    refetchInterval: 60_000,
    enabled,
  });

/**
 * Opening the panel marks it read — and the badge only falls **on the answer**.
 *
 * `setQueryData` with the server's own recomputed feed rather than an optimistic zero: a failed
 * call must leave the unread state exactly where it was. Losing it silently is worse than showing
 * a count for one more minute, because nothing would ever bring it back.
 */
export const useMarkNotificationsSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationsSeen,
    retry: false,
    onSuccess: (feed) => queryClient.setQueryData([KEY], feed),
  });
};
